import React, { useState, useMemo, useEffect } from 'react';
import { Loader2, Plus, Check, ImageOff, ExternalLink, FlaskConical, ArrowUpDown, Info, Package, Truck, CreditCard, Megaphone, RotateCcw, Search, Repeat, TrendingUp, ArrowUp, ArrowDown, Minus, Sparkles, RefreshCw, Rocket, ChevronDown, Bookmark, Trash2 } from 'lucide-react';
import { searchProducts, saveProduct, SearchProduct, analyzeTrend, findMatches, TrendAnalysis, MatchResult, discoverProducts, DiscoveredProduct, addToAutoStoreQueue } from './api';
import { ProductDetailModal } from './ProductDetailModal';
import { useDropGenStore } from '../../stores/dropgenStore';

type SearchMode = 'search' | 'match';

const API_BASE = process.env.NODE_ENV === 'production'
  ? '/api/dropgen'
  : 'http://localhost:8780/api/dropgen';

export function ResearchView() {
  const {
    searchQuery,
    searchProducts: products,
    searchPage,
    searchTotal,
    searchHasMore,
    searchLoading,
    searchSortBy,
    setSearchProducts,
    setSearchLoading,
    setSearchMeta,
    setSearchSortBy,
    setActiveView,
    loadResearch,
    resetAgents,
    setSearchQuery
  } = useDropGenStore();

  // State
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [savingId, setSavingId] = useState<string | null>(null);
  const [queuedIds, setQueuedIds] = useState<Set<string>>(new Set());
  const [queueingId, setQueueingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showBreakdown, setShowBreakdown] = useState<string | null>(null);

  // Search mode (search vs match - discover is default view)
  const [searchMode, setSearchMode] = useState<SearchMode>('search');
  const [localQuery, setLocalQuery] = useState(searchQuery);
  const [showModeDropdown, setShowModeDropdown] = useState(false);

  // Match specific
  const [matchResults, setMatchResults] = useState<MatchResult[]>([]);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchSource, setMatchSource] = useState<'amazon' | 'ebay' | 'aliexpress' | 'manual'>('amazon');

  // Trend data
  const [trendData, setTrendData] = useState<TrendAnalysis | null>(null);
  const [trendLoading, setTrendLoading] = useState(false);

  // Discovery state
  const [discoveredProducts, setDiscoveredProducts] = useState<DiscoveredProduct[]>([]);
  const [discoveryKeywords, setDiscoveryKeywords] = useState<string[]>([]);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [discoveryFetched, setDiscoveryFetched] = useState(false);
  const [discoveryCached, setDiscoveryCached] = useState(false);
  const discoveryRequestId = React.useRef(0);

  // View mode: 'discover' | 'search' | 'match' | 'keyword' | 'saved'
  const [viewMode, setViewMode] = useState<'discover' | 'search' | 'match' | 'keyword' | 'saved'>('discover');
  const [activeKeyword, setActiveKeyword] = useState<string | null>(null);
  const [keywordProducts, setKeywordProducts] = useState<SearchProduct[]>([]);
  const [keywordPage, setKeywordPage] = useState(1);
  const [keywordTotal, setKeywordTotal] = useState(0);
  const [keywordHasMore, setKeywordHasMore] = useState(false);
  const [keywordLoading, setKeywordLoading] = useState(false);

  // Saved products state
  const [savedProducts, setSavedProducts] = useState<any[]>([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [savedFetched, setSavedFetched] = useState(false);

  // Product detail modal
  const [selectedProduct, setSelectedProduct] = useState<any>(null);

  // Sort products
  const sortedProducts = useMemo(() => {
    if (searchSortBy === 'relevance') return products;
    return [...products].sort((a, b) => {
      switch (searchSortBy) {
        case 'roi': return b.roi - a.roi;
        case 'margin': return b.margin - a.margin;
        case 'cost_low': return a.cost - b.cost;
        case 'cost_high': return b.cost - a.cost;
        default: return 0;
      }
    });
  }, [products, searchSortBy]);

  // Pre-load discovery on mount
  useEffect(() => {
    if (!discoveryFetched && !discoveryLoading) {
      handleDiscovery();
    }
  }, []);

  // Fetch trend when searching
  useEffect(() => {
    if (searchQuery && products.length > 0 && viewMode === 'search') {
      fetchTrendData(searchQuery);
    }
  }, [searchQuery, products.length]);

  const fetchTrendData = async (keyword: string) => {
    if (!keyword.trim()) return;
    setTrendLoading(true);
    try {
      const data = await analyzeTrend(keyword.trim());
      setTrendData(data);
    } catch (err) {
      setTrendData(null);
    } finally {
      setTrendLoading(false);
    }
  };

  const handleDiscovery = async (refresh = false) => {
    const requestId = ++discoveryRequestId.current;
    setDiscoveryLoading(true);
    setError(null);
    try {
      const data = await discoverProducts(50, refresh);
      if (requestId !== discoveryRequestId.current) return;
      setDiscoveredProducts(data.products || []);
      setDiscoveryKeywords(data.keywords || []);
      setDiscoveryCached(data.cached || false);
      if (data.error) setError(data.error);
    } catch (err) {
      if (requestId !== discoveryRequestId.current) return;
      setError(err instanceof Error ? err.message : 'Discovery failed');
    } finally {
      if (requestId === discoveryRequestId.current) {
        setDiscoveryLoading(false);
        setDiscoveryFetched(true);
      }
    }
  };

  const loadSavedProducts = async () => {
    setSavedLoading(true);
    try {
      const response = await fetch(`${API_BASE}/products`);
      const data = await response.json();
      setSavedProducts(data.products || []);
    } catch (err) {
      console.error('Failed to load saved products:', err);
    } finally {
      setSavedLoading(false);
      setSavedFetched(true);
    }
  };

  // Load saved on mode switch
  useEffect(() => {
    if (viewMode === 'saved' && !savedFetched) {
      loadSavedProducts();
    }
  }, [viewMode, savedFetched]);

  const handleSearch = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!localQuery.trim() || searchLoading) return;

    setError(null);
    setViewMode('search');

    if (searchMode === 'match') {
      setViewMode('match');
      setMatchLoading(true);
      try {
        const data = await findMatches(localQuery.trim(), matchSource);
        setMatchResults(data.matches || []);
        fetchTrendData(localQuery.trim());
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Match failed');
        setMatchResults([]);
      } finally {
        setMatchLoading(false);
      }
    } else {
      setSearchLoading(true);
      setSearchQuery(localQuery.trim());
      try {
        const data = await searchProducts(localQuery.trim(), 1, 25);
        setSearchProducts(data.products, false);
        setSearchMeta({ page: 1, total: data.total, hasMore: data.hasMore });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Search failed');
      } finally {
        setSearchLoading(false);
      }
    }
  };

  const handleKeywordClick = async (keyword: string) => {
    if (activeKeyword === keyword) {
      setActiveKeyword(null);
      setViewMode('discover');
      return;
    }

    setActiveKeyword(keyword);
    setViewMode('keyword');
    setKeywordLoading(true);
    setKeywordPage(1);
    try {
      const data = await searchProducts(keyword, 1, 25);
      setKeywordProducts(data.products);
      setKeywordTotal(data.total);
      setKeywordHasMore(data.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load keyword');
    } finally {
      setKeywordLoading(false);
    }
  };

  const handleKeywordLoadMore = async () => {
    if (!activeKeyword || keywordLoading) return;
    setKeywordLoading(true);
    const nextPage = keywordPage + 1;
    try {
      const data = await searchProducts(activeKeyword, nextPage, 25);
      setKeywordProducts(prev => [...prev, ...data.products]);
      setKeywordPage(nextPage);
      setKeywordHasMore(data.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more');
    } finally {
      setKeywordLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (!searchQuery.trim() || searchLoading) return;
    try {
      setSearchLoading(true);
      const nextPage = searchPage + 1;
      const data = await searchProducts(searchQuery.trim(), nextPage, 25);
      setSearchProducts(data.products, true);
      setSearchMeta({ page: nextPage, total: data.total, hasMore: data.hasMore });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAddProduct = async (product: SearchProduct) => {
    if (savedIds.has(product.cj_pid)) return;
    setSavingId(product.cj_pid);
    try {
      await saveProduct(product);
      setSavedIds(prev => new Set([...prev, product.cj_pid]));
    } catch (err) {
      setError('Failed to save product');
    } finally {
      setSavingId(null);
    }
  };

  const handleQueueProduct = async (product: SearchProduct | DiscoveredProduct) => {
    const pid = product.cj_pid;
    if (queuedIds.has(pid)) return;
    setQueueingId(pid);
    try {
      await addToAutoStoreQueue([{
        cj_pid: pid,
        sku: (product as SearchProduct).sku || (product as DiscoveredProduct).sku || undefined,
        product_url: product.product_url || undefined,
        name: product.name,
        cost: product.cost,
        category: product.category || 'General',
        image: product.image || '',
        images: product.image ? [product.image] : []
      }]);
      setQueuedIds(prev => new Set([...prev, pid]));
    } catch (err) {
      setError('Failed to add to queue');
    } finally {
      setQueueingId(null);
    }
  };

  const backToDiscover = () => {
    setViewMode('discover');
    setActiveKeyword(null);
    setLocalQuery('');
    setSearchProducts([], false);
    setMatchResults([]);
  };

  const TrendDirectionIcon = ({ direction }: { direction: string }) => {
    if (direction === 'Rising') return <ArrowUp className="w-3.5 h-3.5 text-green-400" />;
    if (direction === 'Falling') return <ArrowDown className="w-3.5 h-3.5 text-red-400" />;
    return <Minus className="w-3.5 h-3.5 text-gray-400" />;
  };

  // Product Card Component
  const ProductCard = ({ product, variant = 'default' }: { product: SearchProduct | DiscoveredProduct; variant?: 'default' | 'discovered' }) => {
    const pid = product.cj_pid;
    const isSaved = savedIds.has(pid);
    const isSaving = savingId === pid;
    const isQueued = queuedIds.has(pid);
    const isQueueing = queueingId === pid;

    const margin = 'margin' in product ? product.margin : (product as DiscoveredProduct).profit_margin;
    const roi = product.roi;
    const netProfit = 'netProfit' in product ? product.netProfit : (product as DiscoveredProduct).net_profit;
    const score = 'score' in product ? (product as DiscoveredProduct).score : null;
    const hasVideo = 'hasVideo' in product ? product.hasVideo : (product as DiscoveredProduct).has_video;

    const searchProduct: SearchProduct = 'margin' in product ? product as SearchProduct : {
      cj_pid: product.cj_pid,
      sku: (product as DiscoveredProduct).sku || product.cj_pid,
      name: product.name,
      image: product.image,
      product_url: product.product_url,
      category: product.category,
      cost: product.cost,
      retail: product.retail,
      netProfit: (product as DiscoveredProduct).net_profit,
      margin: (product as DiscoveredProduct).profit_margin,
      roi: product.roi,
      breakdown: null,
      listedNum: (product as DiscoveredProduct).listed_num,
      hasVideo: (product as DiscoveredProduct).has_video,
      supplierName: (product as DiscoveredProduct).supplier,
      stock: 0,
    };

    return (
      <div className="flex flex-col bg-gray-800/50 rounded-lg border border-gray-700 hover:border-cyan-500/50 transition-all overflow-hidden group">
        <div className="relative aspect-square bg-gray-900">
          {product.image ? (
            <img src={product.image} alt={product.name} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          ) : (
            <div className="w-full h-full flex items-center justify-center"><ImageOff className="w-8 h-8 text-gray-700" /></div>
          )}
          {score !== null && (
            <div className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-bold ${score >= 70 ? 'bg-green-500 text-white' : score >= 50 ? 'bg-yellow-500 text-black' : score >= 30 ? 'bg-orange-500 text-white' : 'bg-red-500 text-white'}`}>
              {score.toFixed(0)}
            </div>
          )}
          {hasVideo && (
            <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-purple-600 rounded text-[10px] text-white font-medium">VIDEO</div>
          )}
        </div>
        <div className="flex flex-col p-3 flex-1">
          <h3 className="text-xs font-medium text-gray-200 line-clamp-2 mb-2">{product.name}</h3>
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-xs text-gray-500">Cost</span>
            <span className="text-sm font-bold text-gray-200">${product.cost.toFixed(2)}</span>
            <span className="text-[10px] text-gray-600">→</span>
            <span className="text-sm font-bold text-green-400">${product.retail.toFixed(2)}</span>
          </div>
          <div className="flex items-center gap-2 text-xs mb-2">
            <span className={`font-bold ${margin >= 30 ? 'text-green-400' : margin >= 15 ? 'text-yellow-400' : 'text-red-400'}`}>
              {margin.toFixed(0)}% margin
            </span>
            <span className="text-gray-600">|</span>
            <span className={`font-bold ${roi >= 80 ? 'text-cyan-400' : 'text-gray-400'}`}>
              {roi.toFixed(0)}% ROI
            </span>
          </div>
          {netProfit !== undefined && (
            <div className="text-xs text-gray-500 mb-3">
              Net: <span className={netProfit > 0 ? 'text-green-400' : 'text-red-400'}>${netProfit.toFixed(2)}</span>
            </div>
          )}
          <div className="mt-auto flex gap-1.5">
            <button
              onClick={() => handleAddProduct(searchProduct)}
              disabled={isSaved || isSaving}
              className={`flex-1 py-1.5 px-2 rounded text-xs font-medium transition-colors flex items-center justify-center gap-1 ${isSaved ? 'bg-green-600/20 text-green-400 border border-green-600/30' : 'bg-cyan-600 hover:bg-cyan-500 text-white'}`}
            >
              {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : isSaved ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3" />}
              {isSaved ? 'Saved' : 'Save'}
            </button>
            <button
              onClick={() => handleQueueProduct(product)}
              disabled={isQueued || isQueueing}
              className={`flex-1 py-1.5 px-2 rounded text-xs font-medium transition-colors flex items-center justify-center gap-1 ${isQueued ? 'bg-purple-600/20 text-purple-400 border border-purple-600/30' : 'bg-purple-600 hover:bg-purple-500 text-white'}`}
            >
              {isQueueing ? <Loader2 className="w-3 h-3 animate-spin" /> : isQueued ? <Check className="w-3 h-3" /> : <Rocket className="w-3 h-3" />}
              {isQueued ? 'Queued' : 'Queue'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-[#0d1117]">
      {/* Search Bar */}
      <div className="px-4 pt-4 pb-3 border-b border-white/10">
        <form onSubmit={handleSearch} className="flex gap-2 max-w-3xl mx-auto">
          <div className="relative flex-1 flex">
            <input
              type="text"
              value={localQuery}
              onChange={(e) => setLocalQuery(e.target.value)}
              placeholder={searchMode === 'match' ? "Paste competitor product name..." : "Search CJ products..."}
              className="flex-1 px-4 py-2.5 bg-gray-800 border border-gray-600 rounded-l-lg text-sm text-gray-200 placeholder-gray-500 focus:outline-none focus:border-cyan-500"
            />
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowModeDropdown(!showModeDropdown)}
                className="h-full px-3 bg-gray-700 border-y border-r border-gray-600 text-gray-400 hover:text-white flex items-center gap-1.5 text-xs"
              >
                {searchMode === 'search' ? <Search className="w-3.5 h-3.5" /> : <Repeat className="w-3.5 h-3.5" />}
                {searchMode === 'search' ? 'Search' : 'Match'}
                <ChevronDown className="w-3 h-3" />
              </button>
              {showModeDropdown && (
                <div className="absolute right-0 top-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-20 min-w-[140px]">
                  <button
                    type="button"
                    onClick={() => { setSearchMode('search'); setShowModeDropdown(false); }}
                    className={`w-full px-3 py-2 text-left text-xs flex items-center gap-2 hover:bg-gray-700 ${searchMode === 'search' ? 'text-cyan-400' : 'text-gray-400'}`}
                  >
                    <Search className="w-3.5 h-3.5" /> Search CJ
                  </button>
                  <button
                    type="button"
                    onClick={() => { setSearchMode('match'); setShowModeDropdown(false); }}
                    className={`w-full px-3 py-2 text-left text-xs flex items-center gap-2 hover:bg-gray-700 ${searchMode === 'match' ? 'text-purple-400' : 'text-gray-400'}`}
                  >
                    <Repeat className="w-3.5 h-3.5" /> Find Match
                  </button>
                </div>
              )}
            </div>
          </div>
          <button
            type="submit"
            disabled={searchLoading || matchLoading || !localQuery.trim()}
            className={`px-5 py-2.5 rounded-r-lg font-medium transition-colors flex items-center gap-2 text-sm ${searchMode === 'match' ? 'bg-purple-600 hover:bg-purple-500' : 'bg-cyan-600 hover:bg-cyan-500'} disabled:bg-gray-700 text-white`}
          >
            {(searchLoading || matchLoading) ? <Loader2 className="w-4 h-4 animate-spin" /> : searchMode === 'match' ? <Repeat className="w-4 h-4" /> : <Search className="w-4 h-4" />}
            {searchMode === 'match' ? 'Find' : 'Search'}
          </button>
        </form>

        {/* Match source selector */}
        {searchMode === 'match' && (
          <div className="flex items-center justify-center gap-2 mt-2">
            <span className="text-xs text-gray-500">Source:</span>
            {(['amazon', 'ebay', 'aliexpress', 'manual'] as const).map((src) => (
              <button
                key={src}
                onClick={() => setMatchSource(src)}
                className={`px-2.5 py-1 rounded text-xs font-medium transition-all ${matchSource === src ? 'bg-purple-600/30 text-purple-300 border border-purple-500/50' : 'bg-gray-800 text-gray-500 hover:text-gray-300'}`}
              >
                {src.charAt(0).toUpperCase() + src.slice(1)}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Trending Keywords */}
      <div className="px-4 py-2 border-b border-white/5">
        <div className="flex items-center gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <button
            onClick={backToDiscover}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              viewMode === 'discover'
                ? 'bg-amber-500 text-black'
                : 'text-gray-500 hover:text-amber-400'
            }`}
          >
            All
          </button>
          <div className="w-px h-4 bg-white/10 shrink-0" />
          {discoveryKeywords.map((kw) => (
            <button
              key={kw}
              onClick={() => handleKeywordClick(kw)}
              className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                activeKeyword === kw
                  ? 'bg-amber-500 text-black'
                  : 'text-gray-400 hover:text-amber-400 hover:bg-white/5'
              }`}
            >
              {kw}
            </button>
          ))}
          <div className="flex-1" />
          <button
            onClick={() => handleDiscovery(true)}
            disabled={discoveryLoading}
            className="shrink-0 p-1.5 rounded-full text-gray-500 hover:text-amber-400 hover:bg-white/5 transition-all"
            title="Refresh trending"
          >
            {discoveryLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
          <div className="w-px h-4 bg-white/10 shrink-0" />
          <button
            onClick={() => setViewMode('saved')}
            className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
              viewMode === 'saved'
                ? 'bg-blue-500 text-white'
                : 'text-gray-500 hover:text-blue-400 hover:bg-white/5'
            }`}
          >
            <Bookmark className="w-3 h-3" />
            {savedProducts.length > 0 && <span>{savedProducts.length}</span>}
          </button>
        </div>
      </div>

      {/* Context Header */}
      <div className="px-4 py-2 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          {viewMode === 'discover' && (
            <>
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span className="text-gray-400">Showing: <span className="text-amber-400 font-medium">Discover</span></span>
              <span className="text-gray-600">• {discoveredProducts.length} scored products</span>
              {discoveryCached && <span className="text-gray-600">(cached)</span>}
            </>
          )}
          {viewMode === 'keyword' && activeKeyword && (
            <>
              <TrendingUp className="w-4 h-4 text-amber-400" />
              <span className="text-gray-400">Showing: <span className="text-amber-400 font-medium">"{activeKeyword}"</span></span>
              <span className="text-gray-600">• {keywordTotal.toLocaleString()} products</span>
            </>
          )}
          {viewMode === 'search' && (
            <>
              <Search className="w-4 h-4 text-cyan-400" />
              <span className="text-gray-400">Showing: <span className="text-cyan-400 font-medium">"{searchQuery}"</span></span>
              <span className="text-gray-600">• {searchTotal.toLocaleString()} products</span>
            </>
          )}
          {viewMode === 'match' && (
            <>
              <Repeat className="w-4 h-4 text-purple-400" />
              <span className="text-gray-400">Showing: <span className="text-purple-400 font-medium">Match Results</span></span>
              <span className="text-gray-600">• {matchResults.length} matches</span>
            </>
          )}
          {viewMode === 'saved' && (
            <>
              <Bookmark className="w-4 h-4 text-blue-400" />
              <span className="text-gray-400">Showing: <span className="text-blue-400 font-medium">Saved Products</span></span>
              <span className="text-gray-600">• {savedProducts.length} products</span>
            </>
          )}
        </div>

        {viewMode === 'saved' && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setSavedFetched(false); loadSavedProducts(); }}
              disabled={savedLoading}
              className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 rounded text-blue-400 text-xs transition-colors"
            >
              {savedLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            </button>
            <button
              onClick={async () => {
                if (confirm('Clear all saved products?')) {
                  await fetch(`${API_BASE}/products/clear-all`, { method: 'DELETE' });
                  setSavedProducts([]);
                }
              }}
              className="flex items-center gap-1.5 px-2.5 py-1 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 rounded text-red-400 text-xs transition-colors"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        )}

        {(viewMode === 'search' || viewMode === 'keyword') && (
          <select
            value={searchSortBy}
            onChange={(e) => setSearchSortBy(e.target.value as any)}
            className="bg-gray-800 border border-gray-700 text-gray-300 text-xs rounded px-2 py-1"
          >
            <option value="relevance">Relevance</option>
            <option value="roi">Highest ROI</option>
            <option value="margin">Highest Margin</option>
            <option value="cost_low">Price: Low → High</option>
            <option value="cost_high">Price: High → Low</option>
          </select>
        )}
      </div>

      {/* Trend Bar */}
      {trendData && (viewMode === 'search' || viewMode === 'match') && (
        <div className="px-4 py-2 bg-gradient-to-r from-gray-800/50 to-transparent border-b border-white/5">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <TrendDirectionIcon direction={trendData.direction ?? 'Stable'} />
              <span className="text-sm font-medium text-gray-200">{trendData.keyword}</span>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span className={`font-bold ${(trendData.trend_score ?? 0) >= 60 ? 'text-green-400' : (trendData.trend_score ?? 0) >= 40 ? 'text-yellow-400' : 'text-gray-400'}`}>
                Score: {(trendData.trend_score ?? 50).toFixed(0)}
              </span>
              <span className={`px-2 py-0.5 rounded ${trendData.direction === 'Rising' ? 'bg-green-500/20 text-green-400' : trendData.direction === 'Falling' ? 'bg-red-500/20 text-red-400' : 'bg-gray-500/20 text-gray-400'}`}>
                {trendData.direction}
              </span>
            </div>
          </div>
        </div>
      )}

      {error && <div className="px-4 py-2 text-red-400 text-xs bg-red-900/20">{error}</div>}

      {/* Results */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* DISCOVER VIEW */}
        {viewMode === 'discover' && (
          discoveryLoading && discoveredProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Loader2 className="w-12 h-12 mb-4 text-amber-500 animate-spin" />
              <p className="text-sm">Discovering trending products...</p>
            </div>
          ) : discoveredProducts.length > 0 ? (
            <div className="grid grid-cols-5 gap-4">
              {discoveredProducts.map((product) => (
                <ProductCard key={product.cj_pid} product={product} variant="discovered" />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Sparkles className="w-12 h-12 mb-4 text-gray-600" />
              <p className="text-sm">No products discovered</p>
            </div>
          )
        )}

        {/* KEYWORD VIEW */}
        {viewMode === 'keyword' && (
          keywordLoading && keywordProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Loader2 className="w-12 h-12 mb-4 text-amber-500 animate-spin" />
              <p className="text-sm">Loading "{activeKeyword}" products...</p>
            </div>
          ) : keywordProducts.length > 0 ? (
            <>
              <div className="grid grid-cols-5 gap-4">
                {keywordProducts.map((product) => (
                  <ProductCard key={product.cj_pid} product={product} />
                ))}
              </div>
              {keywordHasMore && (
                <div className="flex justify-center pt-6">
                  <button
                    onClick={handleKeywordLoadMore}
                    disabled={keywordLoading}
                    className="px-6 py-2 bg-amber-600 hover:bg-amber-500 disabled:bg-gray-700 text-white rounded-lg font-medium text-sm flex items-center gap-2"
                  >
                    {keywordLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                    Load More ({keywordProducts.length} of {keywordTotal.toLocaleString()})
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Sparkles className="w-12 h-12 mb-4 text-gray-600" />
              <p className="text-sm">No products found for "{activeKeyword}"</p>
            </div>
          )
        )}

        {/* SEARCH VIEW */}
        {viewMode === 'search' && (
          searchLoading && products.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Loader2 className="w-12 h-12 mb-4 text-cyan-500 animate-spin" />
              <p className="text-sm">Searching CJ catalog...</p>
            </div>
          ) : sortedProducts.length > 0 ? (
            <>
              <div className="grid grid-cols-5 gap-4">
                {sortedProducts.map((product) => (
                  <ProductCard key={product.cj_pid} product={product} />
                ))}
              </div>
              {searchHasMore && (
                <div className="flex justify-center pt-6">
                  <button
                    onClick={handleLoadMore}
                    disabled={searchLoading}
                    className="px-6 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:bg-gray-700 text-white rounded-lg font-medium text-sm flex items-center gap-2"
                  >
                    {searchLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                    Load More ({products.length} of {searchTotal.toLocaleString()})
                  </button>
                </div>
              )}
            </>
          ) : null
        )}

        {/* MATCH VIEW */}
        {viewMode === 'match' && (
          matchLoading ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Loader2 className="w-12 h-12 mb-4 text-purple-500 animate-spin" />
              <p className="text-sm">Finding CJ matches...</p>
            </div>
          ) : matchResults.length > 0 ? (
            <div className="space-y-3">
              {matchResults.map((match, idx) => {
                const isQueued = queuedIds.has(match.cj_product_id);
                const isQueueing = queueingId === match.cj_product_id;

                return (
                  <div key={match.cj_product_id} className="flex items-center gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700 hover:border-purple-500/50 transition-all">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-600/20 text-purple-400 font-bold text-sm">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-medium text-gray-200 truncate">{match.cj_product_name}</h3>
                      <div className="flex items-center gap-3 mt-1 text-xs">
                        <span className={`px-2 py-0.5 rounded ${match.match_type === 'exact' ? 'bg-green-500/20 text-green-400' : match.match_type === 'similar' ? 'bg-cyan-500/20 text-cyan-400' : 'bg-gray-500/20 text-gray-400'}`}>
                          {match.match_type}
                        </span>
                        <span className="text-gray-500">CJ: ${(match.price_comparison?.cj_price ?? 0).toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-lg font-bold ${(match.confidence ?? 0) >= 70 ? 'text-green-400' : (match.confidence ?? 0) >= 50 ? 'text-yellow-400' : 'text-gray-400'}`}>
                        {(match.confidence ?? 0).toFixed(0)}%
                      </div>
                      <div className="text-xs text-gray-500">confidence</div>
                    </div>
                    <button
                      onClick={() => {
                        const product: SearchProduct = {
                          cj_pid: match.cj_product_id,
                          sku: match.cj_product_id,
                          name: match.cj_product_name,
                          image: '',
                          product_url: match.product_url,
                          category: 'General',
                          cost: match.price_comparison?.cj_price ?? 0,
                          retail: (match.price_comparison?.cj_price ?? 0) * 3,
                          netProfit: 0,
                          margin: 0,
                          roi: 0,
                          breakdown: null,
                          listedNum: 0,
                          hasVideo: false,
                          supplierName: '',
                          stock: 0,
                        };
                        handleQueueProduct(product);
                      }}
                      disabled={isQueued || isQueueing}
                      className={`p-2 rounded transition-colors ${isQueued ? 'bg-purple-600/20 text-purple-400' : 'bg-purple-600 hover:bg-purple-500 text-white'}`}
                    >
                      {isQueueing ? <Loader2 className="w-4 h-4 animate-spin" /> : isQueued ? <Check className="w-4 h-4" /> : <Rocket className="w-4 h-4" />}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Repeat className="w-12 h-12 mb-4 text-gray-600" />
              <p className="text-sm">Enter a competitor product name to find CJ matches</p>
            </div>
          )
        )}

        {/* SAVED VIEW */}
        {viewMode === 'saved' && (
          savedLoading && savedProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Loader2 className="w-12 h-12 mb-4 text-blue-500 animate-spin" />
              <p className="text-sm">Loading saved products...</p>
            </div>
          ) : savedProducts.length > 0 ? (
            <div className="grid grid-cols-5 gap-4">
              {savedProducts.map((product) => {
                const pid = product.cj_product_id || product.id;
                const isQueued = queuedIds.has(pid);
                const isQueueing = queueingId === pid;
                // Handle both old format (stringified array) and new format (URL strings)
                let image = product.images?.[0] || product.image;
                if (image && image.startsWith('[')) {
                  try {
                    const parsed = JSON.parse(image);
                    image = Array.isArray(parsed) ? parsed[0] : image;
                  } catch (e) { /* keep original */ }
                }
                const name = product.title || product.product_name || product.name || 'Untitled';
                const cost = product.cost_usd || product.cost || 0;

                return (
                  <div
                    key={pid}
                    onClick={() => setSelectedProduct(product)}
                    className="flex flex-col bg-gray-800/50 rounded-lg border border-gray-700 hover:border-blue-500/50 transition-all overflow-hidden cursor-pointer"
                  >
                    <div className="aspect-square bg-gray-900 flex items-center justify-center">
                      {image ? (
                        <img
                          src={image}
                          alt={name}
                          className="w-full h-full object-cover"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      ) : (
                        <ImageOff className="w-8 h-8 text-gray-700" />
                      )}
                    </div>
                    <div className="p-3 flex flex-col flex-1">
                      <h3 className="text-xs font-medium text-gray-200 line-clamp-2 mb-2">{name}</h3>
                      <div className="flex items-center justify-between text-xs mb-3">
                        <span className="text-gray-500">Cost: <span className="text-gray-300">${cost.toFixed(2)}</span></span>
                        <span className="text-gray-600">{product.category || 'General'}</span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const queueProduct: SearchProduct = {
                            cj_pid: pid,
                            sku: pid,
                            name,
                            image: image || '',
                            product_url: product.product_url || '',
                            category: product.category || 'General',
                            cost,
                            retail: cost * 3,
                            netProfit: 0,
                            margin: 0,
                            roi: 0,
                            breakdown: null,
                            listedNum: 0,
                            hasVideo: false,
                            supplierName: '',
                            stock: 0,
                          };
                          handleQueueProduct(queueProduct);
                        }}
                        disabled={isQueued || isQueueing}
                        className={`relative z-10 mt-auto py-1.5 px-2 rounded text-xs font-medium transition-colors flex items-center justify-center gap-1 ${
                          isQueued
                            ? 'bg-purple-600/20 text-purple-400 border border-purple-600/30'
                            : 'bg-purple-600 hover:bg-purple-500 text-white'
                        }`}
                      >
                        {isQueueing ? <Loader2 className="w-3 h-3 animate-spin" /> : isQueued ? <Check className="w-3 h-3" /> : <Rocket className="w-3 h-3" />}
                        {isQueued ? 'Queued' : 'Add to Queue'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <Bookmark className="w-12 h-12 mb-4 text-gray-600" />
              <p className="text-sm">No saved products yet</p>
              <p className="text-xs text-gray-600 mt-2">Products you save from Search or Discover will appear here</p>
            </div>
          )
        )}
      </div>

      {/* Product Detail Modal */}
      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          apiBase={API_BASE}
        />
      )}
    </div>
  );
}
